import { useEffect, useReducer } from 'react';
import {
  fetchWorkspacePolicyCheck,
  isClientDisabledFailure,
} from '../../shared/agent-consent/domain/policyClient';
import { useAnalytics } from '../../../Analytics/AnalyticsProvider';
import { getToolsetNames, parseMcpPolicyData } from './policyParse';
import {
  getPolicyRequestKey,
  getPolicyStateForRequest,
  MCP_POLICY_ERROR_KIND,
  policyReducer,
  type McpPolicyErrorKind,
  type PolicyState,
} from './policyState';

import type { ConsentToken } from '#definitions/context';

export type UseWorkspacePolicyParams = {
  token: ConsentToken;
  /** False keeps the check idle, e.g. Agent MCP or no workspaces on offer. */
  isEnabled: boolean;
  /**
   * The site URL or workspace ARI to check, or null when nothing is selected.
   * One per picker — see usePolicyCheckItem.
   */
  targetId: string | null;
  /** Names the target in a client-disabled error. */
  targetName: string | null;
  /**
   * Fail before requesting when the token carries no redirect callback. The
   * site check owns this: it is the one every Atlassian flow runs.
   */
  requiresRedirectCallback?: boolean;
  onToolsetsLoaded: (toolsetNames: Set<string>) => void;
};

/**
 * One /consent/workspace policy check for a single site or Bitbucket
 * workspace. The result is scoped to the target it was requested for, so a new
 * target never inherits the previous one's toolsets or status.
 */
export function useWorkspacePolicy({
  token,
  isEnabled,
  targetId,
  targetName,
  requiresRedirectCallback = false,
  onToolsetsLoaded,
}: UseWorkspacePolicyParams): PolicyState {
  const analyticsClient = useAnalytics();
  const [policyState, dispatch] = useReducer(policyReducer, {
    status: 'idle',
  });
  const requestKey =
    isEnabled && targetId ? getPolicyRequestKey(token.raw, [targetId]) : null;

  useEffect(() => {
    if (token.serviceType !== 'MCP') return;

    if (requestKey == null || !targetId) {
      // Nothing selected, or the check sits this consent out. Stay idle so no
      // request fails and shows a misleading error beside the selector.
      dispatch({ type: 'RESET' });
      return;
    }

    const fail = (errorKind: McpPolicyErrorKind) =>
      analyticsClient.pushTrackEvent({
        actionSubject: 'mcpPolicyCheck',
        action: 'failed',
        source: 'oauthConsentScreen',
        attributes: { reason: errorKind },
      });

    if (requiresRedirectCallback && !token.redirectUrl?.callback) {
      dispatch({
        type: 'ERROR',
        requestKey,
        errorMessage: null,
        errorKind: MCP_POLICY_ERROR_KIND.missingRedirect,
      });
      fail(MCP_POLICY_ERROR_KIND.missingRedirect);
      return;
    }

    const ac = new AbortController();
    dispatch({ type: 'LOADING', requestKey });

    void (async () => {
      try {
        const result = await fetchWorkspacePolicyCheck(
          { contextJwt: token.raw, selectedWorkspace: targetId },
          ac.signal,
        );

        if (ac.signal.aborted) return;

        if (!result.ok) {
          // 403 client_disabled: an admin disabled the app (or its system
          // account) for the org that owns this target.
          const clientDisabled = isClientDisabledFailure(result);
          const errorKind = clientDisabled
            ? MCP_POLICY_ERROR_KIND.clientDisabled
            : MCP_POLICY_ERROR_KIND.policyFetchFailed;
          dispatch({
            type: 'ERROR',
            requestKey,
            errorMessage: result.errorMessage,
            errorKind,
            errorWorkspaceName: clientDisabled ? targetName : null,
          });
          fail(errorKind);
          return;
        }

        if (!result.data.redirectAllowed) {
          dispatch({
            type: 'ERROR',
            requestKey,
            errorMessage: null,
            errorKind: MCP_POLICY_ERROR_KIND.redirectNotAllowed,
          });
          fail(MCP_POLICY_ERROR_KIND.redirectNotAllowed);
          return;
        }

        const { byIntent, crossOrgScopesRemoved } = parseMcpPolicyData(
          result.data,
        );
        dispatch({
          type: 'SUCCESS',
          requestKey,
          byIntent,
          crossOrgScopesRemoved,
        });
        onToolsetsLoaded(getToolsetNames(byIntent));
      } catch (e) {
        if (
          (e instanceof DOMException && e.name === 'AbortError') ||
          ac.signal.aborted
        )
          return;
        dispatch({
          type: 'ERROR',
          requestKey,
          errorMessage: e instanceof Error ? e.message : 'Unexpected error',
          errorKind: MCP_POLICY_ERROR_KIND.unexpected,
        });
      }
    })();

    return () => {
      ac.abort();
    };
  }, [
    token,
    requestKey,
    targetId,
    targetName,
    requiresRedirectCallback,
    analyticsClient,
    onToolsetsLoaded,
  ]);

  return getPolicyStateForRequest(policyState, requestKey);
}
